import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartNOKDay({ title, subtitle, data, chartColors, detailsDesc, orderNumber = "", dateRange = ""  }) {
    // Przykładowe dane - w prawdziwej aplikacji byłyby przekazane jako props
    const defaultData = [
        {
            date: 'n/a',
            A: 'n/a',
            nok: 'n/a',
            rework: 'n/a'
        }
    ];

    const inputData = data || defaultData;

    // Kolory z zapamiętanej palety
    const colors = chartColors || {
        A:   '#FBCEB1',
        B:   '#800080',
        C:   '#FFFF00',
        D:   '#808000',
        E:   '#DC143C',
        F:   '#C0C0C0',
        G:   '#8080C0',
        H:   '#808080',
        I:   '#20B2AA',
        J:   '#C71585',
        K:   '#F5F5DC',
        L:   '#008B8B',
        M:   '#FF0000',
        N:   '#87CEEB',
        O:   '#FA8072',
        P:   '#8CCECB',
        Q:   '#4B0082',
        R:   '#000080',
        S:   '#C08C00',
        T:   '#E6E6FA',
        U:   '#00FFFF',
        V:   '#8B4513',
        W:   '#FF69B4',
        X:   '#00CED1',
        Y:   '#FFD700',
        Z:   '#0000FF',
        a:   '#FBCEB1',
        b:   '#800080',
        c:   '#FFFF00',
        d:   '#808000',
        e:   '#2C143C',
        f:   '#C0C0C0',
        g:   '#F2CD32',
        h:   '#808080',
        i:   '#20B2AA',
        j:   '#C71585',
        k:   '#F5F5DC',
        l:   '#008B8B',
        m:   '#3F0000',
        n:   '#87CEEB',
        o:   '#FA8072',
        p:   '#FF7F00',
        q:   '#4B0082',
        r:   '#000080',
        s:   '#303330',
        t:   '#E6E6FA',
        u:   '#00FFFF',
        v:   '#8B4513',
        w:   '#FF69B4',
        x:   '#00CED1',
        y:   '#FFD700',
        z:   '#0000FF'
    };

    // NOWE: Oblicz sumy dla każdego NOK-a PRZED definicją allNoks
    const nokTotals = {};
    inputData.forEach(day => {
        Object.keys(day).forEach(key => {
            if (key !== 'date' && key !== 'nok' && key !== 'rework') {
                nokTotals[key] = (nokTotals[key] || 0) + (day[key] || 0);
            }
        });
    });

    // Oblicz total parts
    const totalParts = inputData.reduce((sum, day) => {
        const dayTotal = Object.keys(day)
            .filter(key => key !== 'date' && key !== 'nok' && key !== 'rework')
            .reduce((daySum, nok) => daySum + (day[nok] || 0), 0);
        return sum + dayTotal;
    }, 0);

    // Znajdź wszystkie unikalne noki w danych i SORTUJ od największego % do najmniejszego
    const allNoks = Array.from(new Set(
        inputData.flatMap(item =>
            Object.keys(item).filter(key => key !== 'date' && key !== 'nok' && key !== 'rework' && item[key] > 0)
        )
    )).sort((a, b) => (nokTotals[b] || 0) - (nokTotals[a] || 0));

    // Oblicz statystyki
    const totalNok = inputData.reduce((sum, day) => {
        const dayTotal = Object.keys(day)
            .filter(key => key === 'nok')
            .reduce((daySum, nok) => daySum + (day[nok] || 0), 0);
        return sum + dayTotal;
    }, 0);

    const totalRework = inputData.reduce((sum, day) => {
        const dayTotal = Object.keys(day)
            .filter(key => key === 'rework')
            .reduce((daySum, rework) => daySum + (day[rework] || 0), 0);
        return sum + dayTotal;
    }, 0);

    const activeDays = inputData.length;

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Filtruj payload aby wyświetlić tylko elementy z wartością > 0
            const filteredPayload = payload.filter(entry => entry.value > 0);

            const total = filteredPayload.reduce((sum, entry) => sum + entry.value, 0);
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '150px'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'rgb(52, 71, 103)' }}>
                        {`Date: ${label}`}
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                        {`Total: ${total}`}
                    </p>
                    {filteredPayload.reverse().map((entry, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px'
                        }}>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: entry.color,
                                marginRight: '6px',
                                borderRadius: '2px'
                            }}></div>
                            <span style={{ color: '#333', fontSize: '12px' }}>
                            {entry.dataKey} : {entry.value} {totalParts > 0 && nokTotals[entry.dataKey] > 0 && ` (${(nokTotals[entry.dataKey]*100/totalParts).toFixed(1)}%) `}
                                {
                                    detailsDesc && detailsDesc
                                        .find(el => el.label === entry.dataKey)
                                        ?.value
                                }
                        </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Custom legend
    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '16px',
                maxHeight: '120px',
                overflowY: 'auto',
                paddingRight: '8px',
                paddingBottom: '4px'
            }}>
                {payload.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: entry.color,
                            marginRight: '6px',
                            borderRadius: '2px'
                        }}></div>
                        <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                            {entry.dataKey} : {nokTotals[entry.dataKey] || 0} {totalParts > 0 && nokTotals[entry.dataKey] > 0 && ` (${(nokTotals[entry.dataKey]*100/totalParts).toFixed(1)}%) `}

                            {
                                detailsDesc && detailsDesc
                                    .find(el => el.label === entry.dataKey)
                                    ?.value
                            }
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // Styles based on ContactContainer
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        header: {
            marginBottom: '24px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        }
    };

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{activeDays}</p>
                        <p style={styles.statLabel}>Analysis Days</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{title === 'NOK' ? totalNok : totalRework}</p>
                        <p style={styles.statLabel}>Total {title}</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{totalParts}</p>
                        <p style={styles.statLabel}>Total defects no.</p>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={inputData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                        maxBarSize={60}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                            label={{
                                value: 'Parts Count',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fontSize: '12px', fill: 'rgb(123, 128, 154)' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />

                        {allNoks.map((nok, index) => (
                            <Bar
                                key={nok}
                                dataKey={nok}
                                stackId="nok"
                                fill={colors[nok] || '#9E9E9E'}
                                radius={index === allNoks.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default ChartNOKDay;